import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { auth, db, storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { 
  collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, 
  doc, getDoc, setDoc, deleteDoc, updateDoc, arrayUnion, arrayRemove, increment, limit 
} from 'firebase/firestore';

// ImgBB API Key
const IMGBB_API_KEY = "af3aa5305ed243a9489b195b87a2afe5";

// Store media/files in Firebase Storage and keep only URLs in Firestore.
const uploadFileToStorage = (file, path, onProgress = () => {}) => new Promise((resolve, reject) => {
  if (!file) { reject(new Error('No file selected')); return; }
  const safePath = path.replace(/\\/g, '/').replace(/\/\//g, '/');
  const fileRef = storageRef(storage, safePath);
  const task = uploadBytesResumable(fileRef, file, {
    contentType: file.type || 'application/octet-stream',
    cacheControl: 'public,max-age=172800'
  });
  task.on('state_changed', snapshot => {
    if (snapshot.totalBytes) onProgress(Math.min(99, Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)));
  }, reject, async () => {
    try {
      const url = await getDownloadURL(task.snapshot.ref);
      onProgress(100);
      resolve({ url, path: safePath });
    } catch (e) { reject(e); }
  });
});

const uploadToImgBB = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  try {
    const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, { method: 'POST', body: formData });
    const data = await res.json();
    if (data.success) return data.data.url;
    else throw new Error(data.error?.message || 'ImgBB Upload Failed');
  } catch (err) { throw err; }
};

const compressImage = (file, isSticker = false) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width; let height = img.height; 
        const maxWidth = isSticker ? 512 : 1000; 
        if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL(isSticker ? 'image/png' : 'image/jpeg', 0.8));
      };
    };
  });
};

function getSavedPhoneContacts() {
  try { return JSON.parse(localStorage.getItem('nexora_local_contacts') || '{}'); } catch (e) { return {}; }
}

function getCachedData(key) {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch(e) { return []; }
}

function getCommunityCache(commId) {
  const fallback = { messages: [], unreadIds: [], firstUnreadId: null, unreadCount: 0, savedAt: 0 };
  if (!commId) return fallback;
  try {
    const raw = localStorage.getItem(`nexora_comm_msgs_${commId}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return { ...fallback, messages: parsed };
    return {
      messages: Array.isArray(parsed?.messages) ? parsed.messages : [],
      unreadIds: Array.isArray(parsed?.unreadIds) ? parsed.unreadIds : [],
      firstUnreadId: parsed?.firstUnreadId || null,
      unreadCount: Number(parsed?.unreadCount) || 0,
      savedAt: Number(parsed?.savedAt) || 0
    };
  } catch (e) {
    return fallback;
  }
}

const normalizePhone = (p) => {
  if (!p) return '';
  let d = p.replace(/\D/g, '');
  if (d.startsWith('880')) d = d.substring(3);
  if (d.startsWith('0')) d = d.substring(1);
  return `+880${d}`;
};

const CommunityScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { communityId: routeCommunityId } = useParams();

  const [currentUid, setCurrentUid] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isPageSliding, setIsPageSliding] = useState(false); 
  const [isDataLoaded, setIsDataLoaded] = useState(false); // সাদা স্ক্রিন ফিক্স করার জন্য

  // Custom Toast State
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = (msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMsg(msg);
    toastTimerRef.current = setTimeout(() => setToastMsg(null), 2000);
  };

  // কমিউনিটি স্টেট
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [posts, setMessages] = useState([]);

  // ক্রিয়েট মডাল
  const [showCreateModal, setShowNewCreateModal] = useState(false);
  const [commName, setCommName] = useState('');
  const [commDesc, setCommDesc] = useState('');
  const [commImageFile, setCommImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  // এডিট কমিউনিটি মডাল 
  const [showEditCommModal, setShowEditCommModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState('');

  // মেম্বারস মডাল
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [selectedMemberUids, setSelectedMemberUids] = useState([]);

  const [allUsersMap, setAllUsersMap] = useState({});
  const [allUsersList, setAllUsersList] = useState([]);

  // কমেন্ট মডাল
  const [activeCommentPost, setActiveCommentPost] = useState(null);
  const [commentsList, setCommentsList] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [replyToComment, setReplyToComment] = useState(null);
  const [commentActionModal, setCommentActionModal] = useState(null);

  // রিয়্যাকশন স্টেট
  const [reactionPopup, setReactionPopup] = useState(null);
  const [customEmojiMsgId, setCustomEmojiMsgId] = useState(null);
  const reactionTimerRef = useRef(null);

  // এডমিন পোস্ট ইনপুট
  const [postText, setPostText] = useState('');
  const [postImageFile, setPostImageFile] = useState(null);
  const [postDocFile, setPostDocFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const textareaRef = useRef(null);

  // Magic Meet Card State
  const [showMeetForm, setShowMeetForm] = useState(false);
  const [meetCompanyName, setMeetCompanyName] = useState('Selfgars');
  const [meetTime, setMeetTime] = useState('২:৩০');
  const [meetLinkInput, setMeetLinkInput] = useState('');

  // Sticker State
  const defaultStickers = [
    "https://cdn-icons-png.flaticon.com/512/4860/4860953.png",
    "https://cdn-icons-png.flaticon.com/512/4860/4860927.png",
    "https://cdn-icons-png.flaticon.com/512/10543/10543085.png"
  ];
  const [savedStickers, setSavedStickers] = useState(getCachedData('nexora_saved_stickers').length ? getCachedData('nexora_saved_stickers') : defaultStickers);
  const [showStickerModal, setShowStickerModal] = useState(false);
  const stickerImportRef = useRef(null);
  const stickerLongPressTimerRef = useRef(null);

  // Voice Recorder
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveBars, setWaveBars] = useState([12, 22, 35, 18, 30, 10, 28, 40, 15, 18]);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [voicePlayback, setVoicePlayback] = useState({});
const mediaRecorderRef = useRef(null);
const audioChunksRef = useRef([]);
const recordingIntervalRef = useRef(null);
const audioContextRef = useRef(null);
const analyserRef = useRef(null);
const animFrameRef = useRef(null);
const micStreamRef = useRef(null);

  // Multi-Select & Actions
  const [messageActionModal, setMessageActionModal] = useState(null); 
  const [msgDetails, setMsgDetails] = useState(null); 
  const [selectedMessageIds, setSelectedMessageIds] = useState([]); 
  const clickTimers = useRef({});
  const clickCounts = useRef({});

  // Forward Modal State
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardTargets, setForwardTargets] = useState([]);
  const [selectedForwardIds, setSelectedForwardIds] = useState([]);
  const [msgToForward, setMsgToForward] = useState(null); 

  const postsEndRef = useRef(null);
  const firstUnreadIdRef = useRef(null);
  const totalUnreadCountRef = useRef(0);
  const observerRef = useRef(null);
  const seenMarkedRef = useRef(new Set());
  const clearedUnreadRef = useRef(false);
  const userInteractedRef = useRef(false);
  const autoClearAllowedRef = useRef(false);
  const autoClearTimerRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const localContacts = getSavedPhoneContacts();
  
  // Cache and scrolling refs
  const isInitialLoadRef = useRef(true);
  const justMounted = useRef(true);

  // প্রি-লোডিং ফাংশন: এটি ব্যাকগ্রাউন্ডে সব কমিউনিটির লেটেস্ট মেসেজগুলো সেভ করে রাখবে
  const preLoadMessages = (commId, uid = currentUid) => {
    const postsRef = collection(db, 'communities', commId, 'posts');
    const q = query(postsRef, orderBy('createdAt', 'asc'), limit(50));
    onSnapshot(q, (snapshot) => {
      const pList = [];
      snapshot.forEach((d) => pList.push({ id: d.id, ...d.data() }));
      try {
        const unreadIds = pList
          .filter(m => m.type !== 'system' && m.senderId !== uid && (!m.seenBy || !m.seenBy[uid]))
          .map(m => m.id);
        localStorage.setItem(`nexora_comm_msgs_${commId}`, JSON.stringify({
          messages: pList,
          unreadIds,
          firstUnreadId: unreadIds[0] || null,
          unreadCount: unreadIds.length,
          savedAt: Date.now()
        }));
      } catch (e) {}
    });
  };

  // Prevent default context menu
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // Handle mount timeout for flawless caching scroll
  useEffect(() => {
    const timer = setTimeout(() => { justMounted.current = false; }, 800);
    return () => clearTimeout(timer);
  }, []);

  // স্লাইড ট্রানজিশন সহ নেভিগেশন
  const handleNavigate = (path, state = {}) => {
    setIsPageSliding(true);
    setTimeout(() => { navigate(path, state); }, 250);
  };

  useEffect(() => {
    const userUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (!userUid) { navigate('/login'); return; }
    setCurrentUid(userUid);

    const initCommunity = async () => {
      const myDoc = await getDoc(doc(db, 'users', userUid));
      if (myDoc.exists()) setCurrentUserData(myDoc.data());

      onSnapshot(collection(db, 'users'), (snapshot) => {
        const uMap = {};
        const uList = [];
        snapshot.forEach((d) => { 
          const uData = d.data();
          uMap[uData.uid] = uData;
          if (uData.uid !== userUid) uList.push(uData);
        });
        setAllUsersMap(uMap);
        setAllUsersList(uList);
      });

      onSnapshot(collection(db, 'communities'), (snapshot) => {
        const cList = [];
        snapshot.forEach((docSnap) => {
          const cData = docSnap.data();
          const isMember = cData.adminUid === userUid || cData.admins?.includes(userUid) || cData.members?.includes(userUid);
          if (isMember) {
            cList.push({ id: docSnap.id, ...cData });
            preLoadMessages(docSnap.id, userUid); // প্রত্যেকটি কমিউনিটির ডাটা ব্যাকগ্রাউন্ডে প্রি-লোড হবে
          }
        });
        setCommunities(cList);
        setIsDataLoaded(true); // ডাটা সম্পূর্ণ লোড হয়েছে

        const requestedCommunityId = routeCommunityId || location.state?.communityId;
        if (requestedCommunityId) {
          const matched = cList.find(c => c.id === requestedCommunityId);
          if (matched) {
            setSelectedCommunity(matched);
            setMessages(getCommunityCache(matched.id).messages);
          }
        }
      });

      const savedChats = getCachedData('nexora_dash_chats');
      const savedGroups = getCachedData('nexora_dash_groups');
      const savedComms = getCachedData('nexora_dash_comms');
      setForwardTargets([...savedChats, ...savedGroups, ...savedComms]);
    };
    initCommunity();
  }, [navigate]);

  // URL পরিবর্তন এবং রিলোড হ্যান্ডলার (সাদা স্ক্রিন সমস্যা চিরতরে দূর করার জন্য)
  useEffect(() => {
    const activeId = routeCommunityId || location.state?.communityId;
    if (activeId && communities.length > 0) {
      const matched = communities.find(c => c.id === activeId);
      if (matched && (!selectedCommunity || selectedCommunity.id !== activeId)) {
        setSelectedCommunity(matched);
        setMessages(getCommunityCache(activeId).messages);
        isInitialLoadRef.current = true;
      }
    } else if (!activeId) {
      setSelectedCommunity(null); // যদি ইউআরএল এ আইডি না থাকে (ব্যাক বাটন), তবে লিস্ট পেইজ দেখাবে
    }
  }, [routeCommunityId, communities, location.state]);

  // অটো-জয়েন হ্যান্ডলার: Community Invite কার্ডে "JOIN COMMUNITY" চাপলে এটা কল হয়
  const handleJoinCommunity = async (communityId) => {
    if (!communityId || !currentUid) return;
    try {
      const commRef = doc(db, 'communities', communityId);
      const commSnap = await getDoc(commRef);
      if (!commSnap.exists()) { showToast("এই কমিউনিটিটি আর নেই!"); return; }

      const commData = commSnap.data();
      const alreadyMember = commData.adminUid === currentUid || commData.admins?.includes(currentUid) || commData.members?.includes(currentUid);

      if (!alreadyMember) {
        await updateDoc(commRef, { members: arrayUnion(currentUid) });
      }

      handleNavigate(`/community/${communityId}`, { state: { communityId } });
    } catch (e) {
      showToast("জয়েন করতে সমস্যা হয়েছে!");
    }
  };

  useEffect(() => {
    if (!selectedCommunity || !currentUid) return;

    const postsRef = collection(db, 'communities', selectedCommunity.id, 'posts');
    const q = query(postsRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const pList = [];
      let foundFirstUnread = false;
      let unreadCount = 0;

      snapshot.forEach((d) => {
        const data = d.data();
        const post = { id: d.id, ...data };
        pList.push(post);

        if (post.senderId !== currentUid && (!post.seenBy || !post.seenBy[currentUid])) {
          unreadCount += 1;
          if (!foundFirstUnread) {
            firstUnreadIdRef.current = post.id;
            foundFirstUnread = true;
          }
        }
      });

      if (!foundFirstUnread) firstUnreadIdRef.current = null;
      totalUnreadCountRef.current = unreadCount;
      setMessages(pList);

      try {
        const cachedPosts = pList.length > 100 ? pList.slice(-100) : pList;
        const unreadIds = pList
          .filter(m => m.type !== 'system' && m.senderId !== currentUid && (!m.seenBy || !m.seenBy[currentUid]))
          .map(m => m.id);
        localStorage.setItem(`nexora_comm_msgs_${selectedCommunity.id}`, JSON.stringify({
          messages: cachedPosts,
          unreadIds,
          firstUnreadId: unreadIds[0] || null,
          unreadCount: unreadIds.length,
          savedAt: Date.now()
        }));
      } catch(e) {}
    });
    return () => unsubscribe();
  }, [selectedCommunity, currentUid]);

  // Opening the community chat consumes this user's unread state, just like ChatScreen.
  // The cached unread anchor is used for the initial position first; after that,
  // all currently unread posts are marked seen and both local caches are cleared.
  const clearCommunityUnread = async (messageList = posts) => {
    if (!selectedCommunity || !currentUid || clearedUnreadRef.current) return;

    const unreadPosts = (Array.isArray(messageList) ? messageList : []).filter(post =>
      post &&
      post.type !== 'system' &&
      post.senderId !== currentUid &&
      (!post.seenBy || !post.seenBy[currentUid])
    );

    if (!unreadPosts.length) {
      totalUnreadCountRef.current = 0;
      firstUnreadIdRef.current = null;
      return;
    }

    clearedUnreadRef.current = true;
    const seenTime = Date.now();

    // Mark every unread post for this user.
    await Promise.all(unreadPosts.map(post =>
      updateDoc(doc(db, 'communities', selectedCommunity.id, 'posts', post.id), {
        [`seenBy.${currentUid}`]: { time: seenTime, listened: false }
      }).catch(() => {})
    ));

    // Authoritative community-level unread counter for this user.
    await updateDoc(doc(db, 'communities', selectedCommunity.id), {
      [`unreadCount.${currentUid}`]: 0
    }).catch(() => {});

    const unreadIdSet = new Set(unreadPosts.map(post => post.id));
    const updatedMessages = (Array.isArray(messageList) ? messageList : []).map(post => {
      if (!unreadIdSet.has(post.id)) return post;
      return {
        ...post,
        seenBy: {
          ...(post.seenBy || {}),
          [currentUid]: { time: seenTime, listened: false }
        }
      };
    });

    totalUnreadCountRef.current = 0;
    firstUnreadIdRef.current = null;
    setMessages(updatedMessages);

    try {
      localStorage.setItem(`nexora_comm_msgs_${selectedCommunity.id}`, JSON.stringify({
        messages: updatedMessages.slice(-100),
        unreadIds: [],
        firstUnreadId: null,
        unreadCount: 0,
        savedAt: Date.now()
      }));
    } catch (e) {}

    // Update Dashboard's local cache immediately; Firestore's snapshot will
    // subsequently provide the same authoritative unreadCount=0.
    try {
      const raw = localStorage.getItem('nexora_dash_comms');
      const dashComms = raw ? JSON.parse(raw) : [];
      if (Array.isArray(dashComms)) {
        localStorage.setItem('nexora_dash_comms', JSON.stringify(dashComms.map(item =>
          item?.id === selectedCommunity.id
            ? { ...item, unreadCount: { ...(item.unreadCount || {}), [currentUid]: 0 } }
            : item
        )));
      }
    } catch (e) {}

    window.dispatchEvent(new CustomEvent('nexoraCommunityUnreadCleared', {
      detail: { communityId: selectedCommunity.id, uid: currentUid, unreadCount: 0 }
    }));
  };

  useEffect(() => {
    if (!selectedCommunity || !currentUid) return;
    clearedUnreadRef.current = false;
    totalUnreadCountRef.current = 0;
    // Keep the cached unread anchor for the initial zero-loading position.
    // It is consumed after the first render below.
  }, [selectedCommunity?.id, currentUid]);

  useEffect(() => {
    if (!selectedCommunity || !currentUid || !posts.length) return;
    const timer = setTimeout(() => { clearCommunityUnread(posts); }, 80);
    return () => clearTimeout(timer);
  }, [selectedCommunity?.id, currentUid, posts.length]);

  // Zero-Loading Scroll Logic
  useLayoutEffect(() => {
    if (posts.length > 0) {
      if (isInitialLoadRef.current || justMounted.current) {
        // mark programmatic scroll so container scroll event won't count it as user interaction
        try {
          programmaticScrollRef.current = true;
          const container = document.querySelector('.scrollable-content');
          const unreadId = firstUnreadIdRef.current;
          if (unreadId && container) {
            const unreadEl = document.getElementById(`comm-post-${unreadId}`);
            if (unreadEl) {
              container.scrollTop = Math.max(0, unreadEl.offsetTop - 120);
            } else {
              postsEndRef.current?.scrollIntoView({ behavior: 'auto' });
            }
          } else {
            postsEndRef.current?.scrollIntoView({ behavior: 'auto' });
          }
          setTimeout(() => { programmaticScrollRef.current = false; }, 600);
        } catch(e) { programmaticScrollRef.current = false; }
        isInitialLoadRef.current = false;
      } else {
        try { programmaticScrollRef.current = true; postsEndRef.current?.scrollIntoView({ behavior: 'smooth' }); setTimeout(() => { programmaticScrollRef.current = false; }, 600); } catch(e) { programmaticScrollRef.current = false; }
      }
    }
  }, [posts]);

  // Textarea Auto Resize Handler
  const handleInputChange = (e) => {
    const val = e.target.value;
    setPostText(val);
    if (textareaRef.current) {
      textareaRef.current.style.height = '20px';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  // Link & Number Rendering with Custom Toast
  const handlePhoneNumberClick = async (rawPhoneStr) => {
    let digitsOnly = rawPhoneStr.replace(/\D/g, '');
    if (digitsOnly.startsWith('880')) digitsOnly = digitsOnly.substring(3);
    if (digitsOnly.startsWith('0')) digitsOnly = digitsOnly.substring(1);
    const formattedPhone = `+880${digitsOnly}`;

    try {
      const userDocRef = doc(db, 'users', formattedPhone);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const tId = userDocSnap.data().uid;
        handleNavigate(`/chat/${tId}`);
      } else { window.location.href = `tel:${formattedPhone}`; }
    } catch (err) { window.location.href = `tel:${formattedPhone}`; }
  };

  const handleLinkTouchStart = (e, textStr) => {
    e.stopPropagation();
    clickTimers.current['link'] = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      navigator.clipboard.writeText(textStr);
      showToast("Copied!");
    }, 500);
  };
  const handleLinkTouchEnd = (e) => {
    e.stopPropagation();
    clearTimeout(clickTimers.current['link']);
  };

  const renderTextWithLinks = (textStr) => {
    if (!textStr) return '';
    return textStr.split('\n').map((line, lineIndex) => {
      const complexRegex = /(https?:\/\/[^\s]+|(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?|(?:\+880|880|0)1[3-9]\d{8}|@[a-zA-Z0-9_.-]+|@All Mention|@\w+\s\w+|\*\*.*?\*\*|\*[^*]+\*)/g;
      const parts = line.split(complexRegex);
      
      return (
        <React.Fragment key={lineIndex}>
          {parts.map((part, i) => {
            if (!part) return null;
            if (part.match(/^(?:\+880|880|0)1[3-9]\d{8}$/)) {
              return <span key={i} onTouchStart={(e) => handleLinkTouchStart(e, part)} onTouchEnd={handleLinkTouchEnd} onClick={(e) => { e.stopPropagation(); handlePhoneNumberClick(part); }} style={{ color: '#2563eb', fontWeight: '800', textDecoration: 'underline', cursor: 'pointer', pointerEvents: 'auto' }}>{part}</span>;
            }

            else if (/^\d+$/.test(part)) {
  return (
    <span
      key={i}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(part);
        showToast("Copied!");
      }}
      style={{
        color: '#2563eb',
        fontWeight: '800',
        textDecoration: 'underline',
        cursor: 'pointer',
        pointerEvents: 'auto'
      }}
    >
      {part}
    </span>
  );
}
            else if (part.match(/^(https?:\/\/[^\s]+|(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)$/)) {
              const href = part.startsWith('http') ? part : 'https://' + part;
              return <span key={i} onTouchStart={(e) => handleLinkTouchStart(e, part)} onTouchEnd={handleLinkTouchEnd} onClick={(e)=>{ e.stopPropagation(); window.open(href, '_blank'); }} style={{ color: '#2563eb', fontWeight: '800', textDecoration: 'underline', cursor: 'pointer', pointerEvents: 'auto' }}>{part}</span>;
            } 
            else if (part.match(/^@([a-zA-Z0-9_.-]+|All Mention|\w+\s\w+)$/)) {
              return <span key={i} style={{ color: '#16a34a', fontWeight: '900', background: '#dcfce7', padding: '1px 6px', borderRadius: '6px' }}>{part}</span>;
            } 
            else if (part.startsWith('**') && part.endsWith('**')) { return <strong key={i}>{part.slice(2, -2)}</strong>; } 
            else if (part.startsWith('*') && part.endsWith('*')) { return <strong key={i}>{part.slice(1, -1)}</strong>; }
            return <span key={i}>{part}</span>;
          })}
          <br />
        </React.Fragment>
      );
    });
  };

  const toggleSelection = (msgId) => {
    if(selectedMessageIds.includes(msgId)) setSelectedMessageIds(selectedMessageIds.filter(id => id !== msgId));
    else setSelectedMessageIds([...selectedMessageIds, msgId]);
  };

  const handleMsgClick = (e, msg) => {
    e.stopPropagation();
    const id = msg.id;
    if (selectedMessageIds.length > 0) { toggleSelection(id); return; }
    clickCounts.current[id] = (clickCounts.current[id] || 0) + 1;
    if (clickCounts.current[id] === 3) {
      if (navigator.vibrate) navigator.vibrate(50);
      setMessageActionModal(msg); clickCounts.current[id] = 0;
    }
    clearTimeout(clickTimers.current[id]);
    clickTimers.current[id] = setTimeout(() => { clickCounts.current[id] = 0; }, 400);
  };

  // Multi-Select Actions
  const handleDeleteMessage = async (msgId) => {
    if (!selectedCommunity) return;
    if (await window.nexoraConfirm("মেসেজটি ডিলেট করতে চান?")) {
      try { await deleteDoc(doc(db, 'communities', selectedCommunity.id, 'posts', msgId)); setMessageActionModal(null); } catch(e){}
    }
  };

  const handleMultiDelete = async () => {
    if (!selectedCommunity) return;
    if (await window.nexoraConfirm(`${selectedMessageIds.length} টি মেসেজ ডিলেট করতে চান?`)) {
        for(let id of selectedMessageIds) await deleteDoc(doc(db, 'communities', selectedCommunity.id, 'posts', id)).catch(()=>{});
        setSelectedMessageIds([]);
    }
  };

  const handleMultiCopy = () => {
    let combinedText = [];
    selectedMessageIds.forEach(id => {
        const m = posts.find(x => x.id === id);
        if(m && m.type === 'text' && m.text) combinedText.push(m.text);
        else if (m && m.text) combinedText.push(m.text);
    });
    if(combinedText.length > 0) {
        navigator.clipboard.writeText(combinedText.join('\n\n'));
        showToast("Copied!");
    }
    setSelectedMessageIds([]);
  };

  const handleMultiForward = () => {
    const msgsToFwd = selectedMessageIds.map(id => posts.find(x => x.id === id)).filter(x => x);
    setMsgToForward(msgsToFwd); 
    setShowForwardModal(true);
  };

  const handleForwardMessageAction = async () => {
    if (selectedForwardIds.length === 0 || !msgToForward) return;
    setIsUploading(true);
    try {
      const msgsArray = Array.isArray(msgToForward) ? msgToForward : [msgToForward];

      for (let targetId of selectedForwardIds) {
        const target = forwardTargets.find(t => t.id === targetId);
        if (!target) continue;
        const path = target.isGroup ? 'groups' : target.isCommunity ? 'communities' : 'chats';
        
        for(let m of msgsArray) {
            const msgData = {
              senderId: currentUid, senderName: currentUserData?.fullName || 'User', senderPhoto: currentUserData?.photoURL || '',
              type: m.type || (m.mediaUrl ? 'image' : 'text'), createdAt: serverTimestamp(), timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
              text: m.text || null, mediaData: m.mediaUrl || m.mediaData || null, fileData: m.fileData || null, fileName: m.fileName || null, audioData: m.audioData || null, duration: m.duration || null,
              companyName: m.companyName || null, meetTime: m.meetTime || null, meetLink: m.meetLink || null, 
              inviteGroupId: m.inviteGroupId || null, groupName: m.groupName || null,
              inviteCommunityId: m.inviteCommunityId || null, communityName: m.communityName || null,
              isForwarded: true, status: 'sent'
            };
            await addDoc(collection(db, path, target.id, 'messages'), msgData);
        }
        await updateDoc(doc(db, path, target.id), { lastMessage: `${currentUserData?.fullName || 'User'}: Forwarded message`, updatedAt: serverTimestamp() }).catch(()=>{});
      }
      showToast("Forwarded successfully!");
      setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); setMessageActionModal(null); 
      setSelectedMessageIds([]); 
    } catch (e) {} finally { setIsUploading(false); }
  };

  // Sticker Logics
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (!file) continue;
        e.preventDefault();
        setIsUploading(true);
        const compressedImg = await compressImage(file, true); 
        const blob = await (await fetch(compressedImg)).blob();
        const newFile = new File([blob], 'pasted_sticker.png', { type: 'image/png' });
        const uploadedUrl = await uploadToImgBB(newFile);
        if (uploadedUrl) { pushPostToFirestore('sticker', { mediaUrl: uploadedUrl }, '🎇 Sticker'); }
        setIsUploading(false);
        break;
      }
    }
  };

  const handleCreateSticker = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    const compressedImg = await compressImage(file, true);
    const blob = await (await fetch(compressedImg)).blob();
    const newFile = new File([blob], 'sticker.png', { type: 'image/png' });
    const uploadedUrl = await uploadToImgBB(newFile);
    setIsUploading(false);
    if (uploadedUrl) {
      const newStickers = [uploadedUrl, ...savedStickers];
      setSavedStickers(newStickers);
      try{ localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); }catch(e){}
      showToast("Sticker saved successfully!");
    }
  };

  const handleSendSticker = (url) => { setShowStickerModal(false); pushPostToFirestore('sticker', { mediaUrl: url }, '🎇 Sticker'); };
  
  const saveSomeoneSticker = (url) => {
    if(!savedStickers.includes(url)) {
      const newStickers = [url, ...savedStickers];
      setSavedStickers(newStickers); 
      try{ localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); }catch(e){}
      showToast("Sticker saved!");
    } else { showToast("Sticker already saved!"); }
    setMessageActionModal(null);
  };
  
  const deleteSavedSticker = async (url) => {
    if (await window.nexoraConfirm("স্টিকারটি ডিলেট করতে চান?")) {
      const newStickers = savedStickers.filter(s => s !== url);
      setSavedStickers(newStickers); 
      try{ localStorage.setItem('nexora_saved_stickers', JSON.stringify(newStickers)); }catch(e){}
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) { setCommImageFile(file); setImagePreview(URL.createObjectURL(file)); }
  };
  const handleEditLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) { setEditLogoFile(file); setEditLogoPreview(URL.createObjectURL(file)); }
  };

  const handleCreateCommunity = async (e) => {
    e.preventDefault();
    if (!commName.trim() || !currentUid) return;
    setIsUploading(true);
    try {
      let iconURL = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=300";
      if (commImageFile) iconURL = await uploadToImgBB(commImageFile);
      const newCommRef = doc(collection(db, 'communities'));
      await setDoc(newCommRef, {
        id: newCommRef.id, name: commName, description: commDesc || 'Official Nexora Community',
        iconURL, createdBy: currentUid, adminUid: currentUid, admins: [currentUid], members: [currentUid],
        createdAt: serverTimestamp(), updatedAt: serverTimestamp()
      });
      setIsUploading(false); setShowNewCreateModal(false); setCommName(''); setCommDesc(''); setCommImageFile(null); setImagePreview('');
      showToast("কমিউনিটি সফলভাবে তৈরি হয়েছে!");
    } catch (err) { setIsUploading(false); }
  };

  const handleUpdateCommunityInfo = async (e) => {
    e.preventDefault();
    if (!selectedCommunity || !editName.trim()) return;
    setIsUploading(true);
    try {
      let iconURL = selectedCommunity.iconURL;
      if (editLogoFile) iconURL = await uploadToImgBB(editLogoFile);
      const commRef = doc(db, 'communities', selectedCommunity.id);
      await updateDoc(commRef, { name: editName, description: editDesc, iconURL, updatedAt: serverTimestamp() });
      setSelectedCommunity(prev => ({ ...prev, name: editName, description: editDesc, iconURL }));
      setIsUploading(false); setShowEditCommModal(false); showToast("কমিউনিটির তথ্য আপডেট হয়েছে!");
    } catch (err) { setIsUploading(false); ; }
  };

  const toggleSelectMember = (uid) => {
    if (selectedMemberUids.includes(uid)) setSelectedMemberUids(prev => prev.filter(id => id !== uid)); 
    else setSelectedMemberUids(prev => [...prev, uid]); 
  };

  const handleAddSelectedMembers = async () => {
    if (!selectedCommunity || selectedMemberUids.length === 0) return;
    setIsUploading(true);
    try {
      const commRef = doc(db, 'communities', selectedCommunity.id);
      await updateDoc(commRef, { members: arrayUnion(...selectedMemberUids) });
      setSelectedCommunity(prev => ({ ...prev, members: [...(prev.members || []), ...selectedMemberUids] }));
      setSelectedMemberUids([]); setShowAddMemberModal(false); setIsUploading(false);
      showToast(`${selectedMemberUids.length} জন সদস্য যুক্ত করা হয়েছে!`);
    } catch (err) { setIsUploading(false); }
  };

  const handleLeaveCommunity = async () => {
    if (!selectedCommunity || !currentUid) return;
  
    try {
      const commRef = doc(db, 'communities', selectedCommunity.id);
      await updateDoc(commRef, { members: arrayRemove(currentUid), admins: arrayRemove(currentUid) });
      setSelectedCommunity(null); setShowMembersModal(false); setShowEditCommModal(false);
    } catch (e) {}
  };

  // Voice logic
const formatVoiceTime = (seconds = 0) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const pauseOtherAudios = (currentAudio) => {
  const audios = document.querySelectorAll('audio');

  audios.forEach(audio => {
    if (audio !== currentAudio) {
      audio.pause();
    }
  });
};

const handleAudioPlay = (e) => {
  const audio = e.currentTarget;

  pauseOtherAudios(audio);

  const id = audio.id?.replace('voice-', '');

  if (!id) return;

  setPlayingVoiceId(id);

  setVoicePlayback(prev => ({
    ...prev,
    [id]: {
      ...(prev[id] || {}),
      current: Number.isFinite(audio.currentTime)
        ? audio.currentTime
        : 0,
      duration: Number.isFinite(audio.duration)
        ? audio.duration
        : prev[id]?.duration || 0
    }
  }));
};

const handleVoiceTimeUpdate = (e, id) => {
  const audio = e.currentTarget;

  const current = Number.isFinite(audio.currentTime)
    ? audio.currentTime
    : 0;

  const duration = Number.isFinite(audio.duration)
    ? audio.duration
    : 0;

  setVoicePlayback(prev => ({
    ...prev,
    [id]: {
      ...(prev[id] || {}),
      current,
      duration: duration || prev[id]?.duration || 0
    }
  }));
};

const handleVoiceLoadedMetadata = (e, id) => {
  const audio = e.currentTarget;

  const duration = Number.isFinite(audio.duration)
    ? audio.duration
    : 0;

  if (!duration) return;

  setVoicePlayback(prev => ({
    ...prev,
    [id]: {
      ...(prev[id] || {}),
      current: prev[id]?.current || 0,
      duration
    }
  }));
};

const handleVoiceEnded = (id) => {
  setPlayingVoiceId(null);

  setVoicePlayback(prev => ({
    ...prev,
    [id]: {
      ...(prev[id] || {}),
      current: 0
    }
  }));
};

const toggleVoicePlayback = (id) => {
  const audio = document.getElementById(`voice-${id}`);

  if (!audio) return;

  if (!audio.paused) {
    audio.pause();
    setPlayingVoiceId(null);
    return;
  }

  pauseOtherAudios(audio);

  audio.play()
    .then(() => {
      setPlayingVoiceId(id);

      setVoicePlayback(prev => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          current: audio.currentTime || 0,
          duration: Number.isFinite(audio.duration)
            ? audio.duration
            : prev[id]?.duration || 0
        }
      }));
    })
    .catch(error => {
      console.warn('Voice playback failed:', error);
      setPlayingVoiceId(null);
    });
};
const makeFallbackWaveform = (id) => {
  let seed = 0;
  String(id || 'voice').split('').forEach(ch => { seed = ((seed * 31) + ch.charCodeAt(0)) >>> 0; });
  const bars = [];
  for (let i = 0; i < 36; i += 1) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    bars.push(8 + (seed % 30));
  }
  return bars;
};

const startMicPreviewStream = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });

    micStreamRef.current = stream;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx();

    if (audioCtx.state === 'suspended') {
      await audioCtx.resume();
    }

    const analyser = audioCtx.createAnalyser();

    // বেশি sensitive live waveform
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.35;
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;

    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);

    audioContextRef.current = audioCtx;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateWave = () => {
      if (!analyserRef.current) return;

      analyser.getByteFrequencyData(dataArray);

      const bars = Array.from(
        dataArray.slice(0, 18)
      ).map((value) => {

        // একদম নীরব অবস্থার noise floor
        const noiseFloor = 2;

        if (value <= noiseFloor) {
          return 5;
        }

        // 0 → 1
        let level =
          (value - noiseFloor) /
          (255 - noiseFloor);

        // নরম গলার শব্দকে বেশি visible করা
        level = Math.pow(level, 0.48);

        // sensitivity boost
        level = Math.min(1, level * 2.2);

        // 5px → 36px
        return Math.round(5 + level * 31);
      });

      setWaveBars(bars);

      animFrameRef.current =
        requestAnimationFrame(updateWave);
    };

    updateWave();

    return stream;

  } catch (e) {
    console.error('Microphone preview failed:', e);
    showToast("Microphone permission denied!");
    setShowVoiceModal(false);
    return null;
  }
};
  const stopMicPreviewStream = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close().catch(() => {});
  };
  const openVoiceModal = async () => { setShowVoiceModal(true); setIsRecording(false); setRecordingTime(0); await startMicPreviewStream(); };
  
  const handleCircleVoiceClick = async () => {
    if (isRecording) { stopAndSendVoice(); } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];
        mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        mediaRecorderRef.current.onstop = () => {
          stopMicPreviewStream();
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const reader = new FileReader(); reader.readAsDataURL(audioBlob);
          (async () => {
            setIsUploading(true);
            setUploadProgress(1);
            try {
              const path = `communityVoice/${selectedCommunity?.id}/${currentUid}/${Date.now()}_${Math.random().toString(36).slice(2, 9)}.webm`;
              const uploaded = await uploadFileToStorage(audioBlob, path, p => setUploadProgress(p));
              await pushPostToFirestore('voice', { audioData: uploaded.url, audioPath: uploaded.path, duration: `${recordingTime}s`, waveform: [...waveBars] }, '🎙️ Voice message');
              setUploadProgress(100);
              setTimeout(() => setUploadProgress(0), 500);
            } catch (err) {
              console.error('Community voice upload failed:', err);
              setUploadProgress(0);
              showToast('ভয়েস পাঠানো যায়নি। আবার চেষ্টা করুন।');
            } finally {
              setIsUploading(false);
            }
          })();
          stream.getTracks().forEach((t) => t.stop());
        };
        mediaRecorderRef.current.start(); setIsRecording(true); setRecordingTime(0);
        recordingIntervalRef.current = setInterval(() => { setRecordingTime((prev) => prev + 1); }, 1000);
      } catch (err) {}
    }
  };
  const stopAndSendVoice = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(recordingIntervalRef.current); setShowVoiceModal(false); }
  };
  const cancelVoiceModal = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.onstop = null; mediaRecorderRef.current.stop(); }
    stopMicPreviewStream(); clearInterval(recordingIntervalRef.current); setIsRecording(false); setShowVoiceModal(false);
  };

  const pushPostToFirestore = async (type, payloadExtra, summaryText) => {
    if (!selectedCommunity || !currentUid) return;
    try {
      await addDoc(collection(db, 'communities', selectedCommunity.id, 'posts'), {
        senderId: currentUid,
        senderName: currentUserData?.fullName || 'Community Admin',
        senderPhoto: currentUserData?.photoURL || '',
        type,
        createdAt: serverTimestamp(),
        timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        reactions: {}, commentsCount: 0,
        ...payloadExtra
      });
      const unreadUpdates = {};
      const allMembers = [selectedCommunity.adminUid, ...(selectedCommunity.admins || []), ...(selectedCommunity.members || [])]
        .filter(Boolean)
        .filter((uid, idx, arr) => uid !== currentUid && arr.indexOf(uid) === idx);
      allMembers.forEach((uid) => { unreadUpdates[`unreadCount.${uid}`] = increment(1); });
      await updateDoc(doc(db, 'communities', selectedCommunity.id), { lastMessage: summaryText, updatedAt: serverTimestamp(), ...unreadUpdates }).catch(() => {});
    } catch (err) {
      console.error('Community pushPostToFirestore failed:', err);
      throw err;
    }
  };

  const handlePostAnnouncement = async (e) => {
    e.preventDefault();
    const txt = postText.trim();
    if (!selectedCommunity || !currentUid) return;

    // Community Invite Card Shortcut & Paste intercept
    if (txt === '@invite') {
      pushPostToFirestore('community_invite', { communityName: selectedCommunity.name, inviteCommunityId: selectedCommunity.id }, '🔗 Community Invite');
      setPostText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }
    if (txt.startsWith('@@community_card|')) {
      const parts = txt.split('|');
      pushPostToFirestore('community_invite', { communityName: parts[1], inviteCommunityId: parts[2] }, '🔗 Community Invite');
      setPostText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    // Magic Cards intercept
    if (txt === '@link') {
      setShowMeetForm(true); setPostText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }
    if (txt.startsWith('@@meet_card|')) {
      const parts = txt.split('|');
      pushPostToFirestore('meet_card', { companyName: parts[1], meetTime: parts[2], meetLink: parts[3] }, '📅 Live Meeting');
      setPostText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px'; 
      return;
    }

    if (!txt && !postImageFile && !postDocFile) return;

    if (postImageFile) {
      const file = postImageFile;
      setIsUploading(true);
      setUploadProgress(1);
      try {
        const isVideo = file.type.startsWith('video/');
        let uploadFile = file;
        if (!isVideo && file.type.startsWith('image/')) {
          const compressed = await compressImage(file);
          const blob = await (await fetch(compressed)).blob();
          uploadFile = new File([blob], `image_${Date.now()}.jpg`, { type: 'image/jpeg' });
          setUploadProgress(5);
        }
        const safeName = (file.name || 'media').replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `communityMedia/${selectedCommunity.id}/${currentUid}/${Date.now()}_${Math.random().toString(36).slice(2, 9)}_${safeName}`;
        const uploaded = await uploadFileToStorage(uploadFile, path, p => setUploadProgress(p));
        await pushPostToFirestore(isVideo ? 'video' : 'image', { mediaUrl: uploaded.url, mediaPath: uploaded.path, text: txt, fileName: file.name, fileSize: (file.size / (1024 * 1024)).toFixed(1) + ' MB' }, isVideo ? '🎥 Video' : '📷 Photo');
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 500);
      } catch (err) {
        console.error('Community media upload failed:', err);
        setUploadProgress(0);
        showToast('ছবি/ভিডিও পাঠানো যায়নি। আবার চেষ্টা করুন।');
      } finally {
        setIsUploading(false);
        setPostText(''); setPostImageFile(null);
        if (textareaRef.current) textareaRef.current.style.height = '20px';
      }
    } else if (postDocFile) {
      const file = postDocFile;
      setIsUploading(true);
      setUploadProgress(1);
      try {
        const safeName = (file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `communityFiles/${selectedCommunity.id}/${currentUid}/${Date.now()}_${Math.random().toString(36).slice(2, 9)}_${safeName}`;
        const uploaded = await uploadFileToStorage(file, path, p => setUploadProgress(p));
        await pushPostToFirestore('file', { fileData: uploaded.url, filePath: uploaded.path, fileName: file.name, fileSize: (file.size / (1024 * 1024)).toFixed(1) + ' MB', text: txt }, `📄 ${file.name}`);
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 500);
      } catch (err) {
        console.error('Community file upload failed:', err);
        setUploadProgress(0);
        showToast('ফাইল পাঠানো যায়নি। আবার চেষ্টা করুন।');
      } finally {
        setIsUploading(false);
        setPostText(''); setPostDocFile(null);
        if (textareaRef.current) textareaRef.current.style.height = '20px';
      }
    } else {
      try {
        await pushPostToFirestore('text', { text: txt }, txt);
      } catch (err) { showToast('পোস্ট পাঠানো যায়নি।'); }
      setPostText('');
      if (textareaRef.current) textareaRef.current.style.height = '20px';
    }
  };

  const handleSendMeetCard = () => {
    if(!meetLinkInput) { ; return; }
    setShowMeetForm(false);
    pushPostToFirestore('meet_card', { companyName: meetCompanyName, meetTime: meetTime, meetLink: meetLinkInput }, '📅 Live Meeting');
    setMeetLinkInput('');
  };

  const handleToggleLoveReaction = async (postId, currentReactions = {}) => {
    if (!selectedCommunity || !currentUid) return;
    const postRef = doc(db, 'communities', selectedCommunity.id, 'posts', postId);
    const existingReaction = currentReactions[currentUid];
    try {
      if (existingReaction === '❤️') {
        const updated = { ...currentReactions };
        delete updated[currentUid];
        await updateDoc(postRef, { reactions: updated });
      } else { await updateDoc(postRef, { [`reactions.${currentUid}`]: '❤️' }); }
    } catch (e) {}
  };

  const handleReactToPost = async (postId, emoji) => {
    if (!selectedCommunity || !currentUid) return;
    try {
      const postRef = doc(db, 'communities', selectedCommunity.id, 'posts', postId);
      await updateDoc(postRef, { [`reactions.${currentUid}`]: emoji });
    } catch (e) {}
  };

  const handleLoveTouchStart = (e, postId) => {
    if(selectedMessageIds.length > 0) return;
    const touch = e.touches ? e.touches[0] : e;
    const posY = touch.clientY; 
    reactionTimerRef.current = setTimeout(() => { 
      if(navigator.vibrate) navigator.vibrate(50);
      setReactionPopup({ id: postId, y: posY }); 
    }, 450);
  };
  const handleLoveTouchEnd = () => {
    if (reactionTimerRef.current) clearTimeout(reactionTimerRef.current);
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!commentInput.trim() || !activeCommentPost || !selectedCommunity || !currentUid) return;
    try {
      const commentsRef = collection(db, 'communities', selectedCommunity.id, 'posts', activeCommentPost.id, 'comments');
      const userPhone = normalizePhone(currentUserData?.phoneNumber);
      const savedName = localContacts[userPhone] || currentUserData?.fullName || 'Member';
      await addDoc(commentsRef, {
        senderId: currentUid, senderName: savedName, text: replyToComment ? `@${replyToComment.senderName} ${commentInput}` : commentInput,
        replyTo: replyToComment ? replyToComment.senderName : null, createdAt: serverTimestamp(), timeStr: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });
      const postRef = doc(db, 'communities', selectedCommunity.id, 'posts', activeCommentPost.id);
      await updateDoc(postRef, { commentsCount: (activeCommentPost.commentsCount || 0) + 1 }).catch(() => {});
      setCommentInput(''); setReplyToComment(null);
    } catch (e) {}
  };

  const openComments = (post) => {
    setActiveCommentPost(post); setReplyToComment(null); setCommentActionModal(null);
    if (!selectedCommunity) return;
    const commentsRef = collection(db, 'communities', selectedCommunity.id, 'posts', post.id, 'comments');
    const q = query(commentsRef, orderBy('createdAt', 'asc'));
    onSnapshot(q, (snapshot) => {
      const cList = [];
      snapshot.forEach((d) => cList.push({ id: d.id, ...d.data() }));
      setCommentsList(cList);
    });
  };

  const handleCommentClick = (e, comment) => {
    e.stopPropagation();
    if (!isAdmin) return;
    const key = `comment:${comment.id}`;
    clickCounts.current[key] = (clickCounts.current[key] || 0) + 1;
    if (clickCounts.current[key] === 3) {
      if (navigator.vibrate) navigator.vibrate(50);
      setCommentActionModal(comment);
      clickCounts.current[key] = 0;
    }
    clearTimeout(clickTimers.current[key]);
    clickTimers.current[key] = setTimeout(() => { clickCounts.current[key] = 0; }, 500);
  };

  const handleDeleteComment = async (comment) => {
    if (!selectedCommunity || !activeCommentPost || !comment || !isAdmin) return;
    if (!await window.nexoraConfirm('এই কমেন্টটি ডিলেট করতে চান?')) return;
    try {
      await deleteDoc(doc(db, 'communities', selectedCommunity.id, 'posts', activeCommentPost.id, 'comments', comment.id));
      const postRef = doc(db, 'communities', selectedCommunity.id, 'posts', activeCommentPost.id);
      await updateDoc(postRef, { commentsCount: Math.max(0, (activeCommentPost.commentsCount || commentsList.length || 1) - 1) }).catch(() => {});
      setCommentActionModal(null);
    } catch (e) {}
  };

  const openEditModal = () => {
    if (!selectedCommunity) return;
    setEditName(selectedCommunity.name || ''); setEditDesc(selectedCommunity.description || ''); setEditLogoPreview(selectedCommunity.iconURL || '');
    setShowEditCommModal(true);
  };

  const handlePromoteToAdmin = async (memberUid) => {
    if (!selectedCommunity || !currentUid || !isAdmin) return;
  
    try { await updateDoc(doc(db, 'communities', selectedCommunity.id), { admins: arrayUnion(memberUid) }); } catch (e) {}
  };

  const handleRemoveAdmin = async (targetAdminUid) => {
    if (!selectedCommunity || !currentUid || !isAdmin) return;
    if (targetAdminUid === currentUid || targetAdminUid === selectedCommunity.adminUid) return;
    const targetUser = allUsersMap[targetAdminUid] || {};
    const targetGlobalRole = targetUser.role?.toLowerCase();
    if (targetGlobalRole === 'owner' || targetGlobalRole === 'admin') return;
    try {
      await updateDoc(doc(db, 'communities', selectedCommunity.id), { admins: arrayRemove(targetAdminUid) });
      showToast('Admin থেকে Member করা হয়েছে!');
    } catch (e) {}
  };

  const handleRemoveMember = async (targetMemberUid) => {
    if (!selectedCommunity || !currentUid) return;
    if (targetMemberUid === currentUid) return; 
  
    try {
      await updateDoc(doc(db, 'communities', selectedCommunity.id), { members: arrayRemove(targetMemberUid), admins: arrayRemove(targetMemberUid) });
      showToast("মেম্বার রিমুভ করা হয়েছে!");
    } catch (e) { ; }
  };

  const handleDeleteCommunity = async () => {
    if (!selectedCommunity || !currentUid) return;
   
    try {
      await deleteDoc(doc(db, 'communities', selectedCommunity.id));
      setSelectedCommunity(null); setShowMembersModal(false); setShowEditCommModal(false);
    } catch (e) {}
  };

  // Role Based Logic & Permissions
  const isGlobalOwner = currentUserData?.role?.toLowerCase() === 'owner';
  const isGlobalAdmin = currentUserData?.role?.toLowerCase() === 'admin';
  const isAdmin = selectedCommunity?.adminUid === currentUid || selectedCommunity?.admins?.includes(currentUid) || isGlobalOwner || isGlobalAdmin;

  const phonebookKeys = Object.keys(localContacts);
  const nonMemberSavedContacts = allUsersList.filter(u => {
    const normP = normalizePhone(u.phoneNumber);
    const isSavedInPhonebook = phonebookKeys.includes(normP);
    const isNotAlreadyMember = !selectedCommunity?.members?.includes(u.uid);
    return isSavedInPhonebook && isNotAlreadyMember;
  });

  const filteredAddMembersList = nonMemberSavedContacts.filter(u => {
    const normP = normalizePhone(u.phoneNumber);
    const savedName = localContacts[normP] || u.fullName || u.phoneNumber;
    const q = memberSearchQuery.toLowerCase();
    return savedName.toLowerCase().includes(q) || u.phoneNumber.includes(q);
  });

  return (
    <div style={styles.container}>
      <style>{`
        body, html, #root { margin: 0; padding: 0; height: 100dvh; width: 100vw; overflow: hidden; position: fixed; background: #fafafa; }
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; -webkit-tap-highlight-color: transparent; -webkit-touch-callout: none; user-select: none; }
        
        .comm-card { width: 100%; max-width: 480px; height: 100dvh; max-height: 100dvh; background: #fafafa; display: flex; flex-direction: column; position: relative; box-shadow: 0 0 40px rgba(0,0,0,0.04); animation: slideInScreen 0.25s cubic-bezier(0.25, 1, 0.5, 1) forwards; }
        .comm-card.slide-out { animation: slideOutScreen 0.25s cubic-bezier(0.25, 1, 0.5, 1) forwards; }
        @keyframes slideInScreen { from { transform: translateX(30px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideOutScreen { from { transform: translateX(0); opacity: 1; } to { transform: translateX(-100%); opacity: 1; } }
        @media (min-width: 768px) { .comm-card { border-radius: 36px; border: 1px solid #f1f5f9; height: 92vh; max-height: 92vh; } }

        /* Fixed Headers & Footers */
        .comm-header { flex: 0 0 auto; width: 100%; padding: 14px 18px; padding-top: max(16px, env(safe-area-inset-top)); background: #ffffff; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; z-index: 20; }
        .admin-footer-bar { flex: 0 0 auto; width: 100%; padding: 10px 14px; padding-bottom: max(12px, env(safe-area-inset-bottom)); background: #ffffff; border-top: 1px solid #f1f5f9; display: flex; align-items: center; gap: 10px; z-index: 20; }
        .multi-select-bar { flex: 0 0 auto; width: 100%; background: #1e293b; padding: 12px 16px; padding-top: max(16px, env(safe-area-inset-top)); color: white; display: flex; justify-content: space-between; align-items: center; z-index: 100; animation: slideDown 0.2s ease; }
        
        /* Flex 1 Scrollable Content */
        .scrollable-content { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; overflow-anchor: none; -webkit-overflow-scrolling: touch; }
        .post-bubble { background: #ffffff; border-radius: 20px; padding: 14px 16px; border: 1px solid #f1f5f9; box-shadow: 0 2px 10px rgba(0,0,0,0.02); margin-bottom: 12px; position: relative; transition: background 0.2s ease; }
        
        .modal-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px); z-index: 100; display: flex; align-items: flex-end; justify-content: center; padding: 20px; }
        .card-modal { width: 100%; max-width: 480px; background: white; border-radius: 28px; padding: 24px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); margin-bottom: 0; max-height: 85vh; overflow-y: auto; }
        .conte.voicext-menu-card { width: 100%; max-width: 320px; background: #ffffff; border-radius: 20px; padding: 8px 0; box-shadow: 0 10px 40px rgba(0,0,0,0.25); border: 1px solid #f1f5f9; margin: auto; }
        .context-opt { padding: 14px 18px; font-size: 15px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 14px; cursor: pointer; border-bottom: 1px solid #f8fafc; }
        .context-opt:last-child { border-bottom: none; }
        .context-opt:active { background: #f0fdf4; color: #16a34a; }

        .input-field { width: 100%; padding: 12px 16px; background-color: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 16px; font-size: 14px; outline: none; }
        .btn-green { padding: 12px 20px; background: #62b875; color: white; border: none; border-radius: 16px; font-weight: 700; cursor: pointer; }

        .reaction-global-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 100; }
        .floating-reaction-popup { position: fixed; background: #ffffff; padding: 4px 8px; border-radius: 30px; box-shadow: 0 10px 25px rgba(0,0,0,0.18); display: flex; gap: 4px; z-index: 101; border: 1px solid #f1f5f9; animation: popUp 0.18s ease-out; width: max-content; max-width: 90vw; overflow-x: auto; transform: translateX(-50%); }
        @keyframes popUp { from { transform: translateX(-50%) scale(0.6); opacity: 0; } to { transform: translateX(-50%) scale(1); opacity: 1; } }
        .reaction-pop-emoji { background: none; border: none; font-size: 20px; cursor: pointer; transition: transform 0.15s ease; padding: 2px 4px; }
        .reaction-pop-emoji:active { transform: scale(1.3); }

        .voice-message-card { width:260px; min-height:76px; display:flex; align-items:center; gap:9px; padding:8px 10px; border-radius:18px; }
        .voice-message-card.voice-me { background:#62b875; color:#fff; }
        .voice-message-card.voice-other { background:#fff; color:#16a34a; border:1px solid #e2e8f0; }
        .voice-message-card audio { display:none; }
        .voice-play-btn { width:38px; height:38px; border:0; border-radius:50%; flex:0 0 38px; display:flex; align-items:center; justify-content:center; font-size:15px; cursor:pointer; }
        .voice-me .voice-play-btn { background:#fff; color:#62b875; }
        .voice-other .voice-play-btn { background:#22c55e; color:#fff; }
        .voice-wave-wrap { flex:1; min-width:0; }
        .voice-wave { height:42px; display:flex; align-items:center; gap:2px; overflow:hidden; }
        .voice-wave span { width:3px; min-height:5px; border-radius:3px; transition:background .12s linear; }
        .voice-meta { display:flex; justify-content:space-between; font-size:10px; opacity:.86; margin-top:2px; }
        .voice-avatar { width:34px; height:34px; border-radius:50%; object-fit:cover; flex:0 0 34px; }
        .upload-progress-wrap { flex: 0 0 auto; padding: 6px 14px; background: #f0fdf4; border-bottom: 1px solid #dcfce7; }
        .upload-progress-bar { height: 3px; width: 100%; background: #dcfce7; overflow: hidden; border-radius: 3px; }
        .upload-progress-bar > div { height: 100%; background: #22c55e; transition: width 0.15s ease; }

        .footer-pill-input { flex: 1; display: flex; align-items: center; background: #f0f2f5; border-radius: 24px; padding: 0 14px; min-height: 40px; }
        .dark-circle-send-btn { width: 44px; height: 44px; border-radius: 50%; background: rgba(24, 24, 27, 0.8); color: white; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
        
        .voice-card-bottom { width: 100%; max-width: 480px; background: #ffffff; border-radius: 28px 28px 0 0; padding: 20px 24px 28px; box-shadow: 0 -10px 30px rgba(0,0,0,0.12); position: absolute; bottom: 0; text-align: center; border-top: 1px solid #f1f5f9; animation: slideUp 0.25s ease-out; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .voice-circle { width: 96px; height: 96px; border-radius: 50%; background: #ffffff; border: 2px solid #62b875; display: flex; align-items: center; justify-content: center; margin: 12px auto 0; cursor: pointer; transition: all 0.2s; box-shadow: 0 8px 20px rgba(98, 184, 117, 0.1); }
        .voice-circle.recording { border-color: #ef4444; box-shadow: 0 8px 20px rgba(239, 68, 68, 0.2); animation: pulseRed 1.5s infinite; }
        @keyframes pulseRed { 0% { transform: scale(0.96); } 50% { transform: scale(1.04); } 100% { transform: scale(0.96); } }

        /* Custom Toast Notification */
        .custom-toast { position: absolute; bottom: 90px; left: 50%; transform: translateX(-50%); background: rgba(15, 23, 42, 0.9); color: white; padding: 10px 20px; border-radius: 20px; font-size: 13px; font-weight: 600; z-index: 1000; animation: fadeInOut 2s ease forwards; pointer-events: none; box-shadow: 0 4px 15px rgba(0,0,0,0.2); white-space: nowrap; }
        @keyframes fadeInOut { 0% { opacity: 0; transform: translate(-50%, 15px); } 15% { opacity: 1; transform: translate(-50%, 0); } 85% { opacity: 1; transform: translate(-50%, 0); } 100% { opacity: 0; transform: translate(-50%, -15px); } }
        @keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }
        
        .custom-checkbox { width: 22px; height: 22px; border-radius: 50%; border: 2px solid #cbd5e1; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .custom-checkbox.checked { background: #62b875; border-color: #62b875; }

        /* Magic Meet & Stickers */
        .magic-meet-wrapper { background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; width: 100%; max-width: 280px; box-shadow: 0 10px 25px rgba(0,0,0,0.05); text-align: center; margin: 10px 0; }
        .magic-meet-header { background: #f0fdf4; padding: 16px; border-bottom: 2px dashed #bbf7d0; }
        .magic-meet-title { font-size: 20px; font-weight: 900; color: #166534; margin: 0; letter-spacing: 0.5px; }
        .magic-meet-subtitle { font-size: 11px; color: #16a34a; font-weight: 700; background: #ffffff; padding: 4px 10px; border-radius: 20px; display: inline-block; margin-top: 8px; border: 1px solid #bbf7d0; }
        .magic-meet-timebox { display: flex; justify-content: space-around; padding: 12px; background: #ffffff; border-bottom: 1px solid #f1f5f9; }
        .magic-meet-timebox div { display: flex; flexDirection: column; align-items: center; font-size: 11px; color: #64748b; }
        .magic-meet-timebox b { font-size: 14px; color: #0f172a; margin-top: 2px; }
        .magic-meet-steps { padding: 12px 16px; text-align: left; font-size: 12px; color: #334155; font-weight: 600; background: #fafafa; }
        .magic-step-item { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .magic-step-no { background: #16a34a; color: white; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 10px; flex-shrink: 0; }
        .magic-join-btn { width: calc(100% - 24px); margin: 12px; padding: 12px; background: #16a34a; color: white; border: none; border-radius: 12px; font-weight: 900; font-size: 14px; cursor: pointer; box-shadow: 0 4px 15px rgba(22, 163, 74, 0.4); pointer-events: auto; }
      `}</style>

      {toastMsg && <div className="custom-toast">{toastMsg}</div>}
      <input type="file" ref={stickerImportRef} accept="image/*" onChange={handleCreateSticker} style={{ display: 'none' }} />

      {/* Magic Meet Form Modal */}
      {showMeetForm && (
        <div className="modal-overlay" onClick={() => setShowMeetForm(false)} style={{ alignItems: 'center' }}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '28px', padding: '24px', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800', color: '#166534', textAlign: 'center' }}>📅 Create Magic Meet Card</h3>
            <input type="text" placeholder="কোম্পানির নাম (e.g., Selfgars)" value={meetCompanyName} onChange={(e)=>setMeetCompanyName(e.target.value)} className="input-field" style={{ marginBottom: '10px' }} />
            <input type="text" placeholder="মিটিং সময় (e.g., ২:৩০)" value={meetTime} onChange={(e)=>setMeetTime(e.target.value)} className="input-field" style={{ marginBottom: '10px' }} />
            <input type="url" placeholder="Google Meet Link (https://meet...)" value={meetLinkInput} onChange={(e)=>setMeetLinkInput(e.target.value)} className="input-field" style={{ marginBottom: '16px' }} />
            <button onClick={handleSendMeetCard} className="btn-green" style={{ width: '100%', padding: '14px' }}>Generate & Send</button>
          </div>
        </div>
      )}

      {/* Sticker Bottom Sheet */}
      {showStickerModal && (
        <div className="modal-overlay" style={{ alignItems: 'flex-end', padding: 0 }}>
          <div className="voice-card-bottom" style={{ height: '350px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
              <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Stickers</span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => stickerImportRef.current?.click()} style={{ background: '#16a34a', color: '#ffffff', border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 4px 10px rgba(22, 163, 74, 0.3)' }}>
                  {isUploading ? 'Creating...' : '➕ Create Sticker'}
                </button>
                <button onClick={() => setShowStickerModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', paddingBottom: '20px' }}>
              {savedStickers.length === 0 && <p style={{ color: '#64748b', fontSize: '13px' }}>No stickers saved. Click Create to make one!</p>}
              {savedStickers.map((url, i) => (
                <img 
                  key={i} src={url} onClick={() => handleSendSticker(url)} 
                  onContextMenu={(e) => { e.preventDefault(); deleteSavedSticker(url); }}
                  onTouchStart={(e) => {
                    stickerLongPressTimerRef.current = setTimeout(() => { if(navigator.vibrate) navigator.vibrate(50); deleteSavedSticker(url); }, 600);
                  }}
                  onTouchEnd={() => clearTimeout(stickerLongPressTimerRef.current)}
                  alt="Sticker" style={{ width: '70px', height: '70px', cursor: 'pointer', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '4px' }} 
                />
              ))}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>Tap to send. Long press to delete.</div>
          </div>
        </div>
      )}

      {/* ১. নতুন কমিউনিটি ক্রিয়েট মডাল */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowNewCreateModal(false)}>
          <div className="card-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 14px', color: '#0f172a' }}>Create Community</h3>
            <form onSubmit={handleCreateCommunity}>
              <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                <img src={imagePreview || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=300"} alt="Logo" style={{ width: '70px', height: '70px', borderRadius: '24px', objectFit: 'cover', marginBottom: '8px' }} />
                <br />
                <label htmlFor="comm-img" style={{ color: '#62b875', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>📷 Upload Logo</label>
                <input id="comm-img" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>NAME</label>
                <input type="text" placeholder="Community Name" value={commName} onChange={(e) => setCommName(e.target.value)} className="input-field" />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>DESCRIPTION</label>
                <textarea rows="2" placeholder="About this community..." value={commDesc} onChange={(e) => setCommDesc(e.target.value)} className="input-field" style={{ resize: 'none' }} />
              </div>
              <button type="submit" disabled={isUploading} className="btn-green" style={{ width: '100%' }}>{isUploading ? 'Creating...' : 'Create Community'}</button>
              <button type="button" onClick={() => setShowNewCreateModal(false)} style={{ width: '100%', background: 'none', border: 'none', color: '#64748b', marginTop: '10px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {/* ২. কমিউনিটি নেম, বায়ো ও লোগো এডিটর মডাল */}
      {showEditCommModal && selectedCommunity && (
        <div className="modal-overlay" onClick={() => setShowEditCommModal(false)}>
          <div className="card-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 14px', color: '#0f172a' }}>Community Info & Settings</h3>
            {isAdmin ? (
              <form onSubmit={handleUpdateCommunityInfo}>
                <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                  <img src={editLogoPreview || selectedCommunity.iconURL} alt="Logo" style={{ width: '70px', height: '70px', borderRadius: '24px', objectFit: 'cover', marginBottom: '8px' }} />
                  <br />
                  <label htmlFor="edit-comm-img" style={{ color: '#62b875', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>📷 Change Logo</label>
                  <input id="edit-comm-img" type="file" accept="image/*" onChange={handleEditLogoChange} style={{ display: 'none' }} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>NAME</label>
                  <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="input-field" />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>BIO / DESCRIPTION</label>
                  <textarea rows="2" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="input-field" style={{ resize: 'none' }} />
                </div>
                <button type="submit" disabled={isUploading} className="btn-green" style={{ width: '100%', marginBottom: '10px' }}>{isUploading ? 'Updating...' : 'Save Changes'}</button>
              </form>
            ) : (
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <img src={selectedCommunity.iconURL} alt="Logo" style={{ width: '80px', height: '80px', borderRadius: '24px', objectFit: 'cover', margin: '0 auto 10px', display: 'block' }} />
                <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '800' }}>{selectedCommunity.name}</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{selectedCommunity.description}</p>
              </div>
            )}
            <button onClick={() => { setShowEditCommModal(false); setShowMembersModal(true); }} style={{ width: '100%', padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '10px' }}>
              👥 View Members ({selectedCommunity.members?.length || 1})
            </button>
            {isAdmin && (
              <button onClick={(e) => {
                e.preventDefault();
                pushPostToFirestore('community_invite', { communityName: selectedCommunity.name, inviteCommunityId: selectedCommunity.id }, '🔗 Community Invite');
                setShowEditCommModal(false);
                showToast("Invite Link Shared!");
              }} style={{ width: '100%', padding: '12px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '10px' }}>
                🔗 Share Community Link
              </button>
            )}
            {!isAdmin && (
              <button onClick={handleLeaveCommunity} style={{ width: '100%', padding: '12px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '14px', fontWeight: '700', cursor: 'pointer' }}>🚪 Leave Community</button>
            )}
            <button onClick={() => setShowEditCommModal(false)} style={{ width: '100%', background: 'none', border: 'none', color: '#64748b', marginTop: '8px', cursor: 'pointer', fontSize: '13px' }}>Close</button>
          </div>
        </div>
      )}

      {/* ৩. মাল্টি-সিলেক্ট ও সার্চ সহ মেম্বার এড মডাল */}
      {showAddMemberModal && selectedCommunity && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '800', margin: 0, color: '#0f172a' }}>Add Members</h3>
              <button onClick={() => { setShowAddMemberModal(false); setSelectedMemberUids([]); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            <input type="text" placeholder="Search contacts..." value={memberSearchQuery} onChange={(e) => setMemberSearchQuery(e.target.value)} className="input-field" style={{ marginBottom: '12px', padding: '10px 14px' }} />
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '14px' }}>
              {filteredAddMembersList.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>আপনার সেভ করা কন্টাক্টলিস্টের কেউ বাকী নেই!</div>
              ) : (
                filteredAddMembersList.map((u) => {
                  const normP = normalizePhone(u.phoneNumber);
                  const displayName = localContacts[normP] || u.fullName || u.phoneNumber;
                  const isChecked = selectedMemberUids.includes(u.uid);
                  return (
                    <div key={u.uid} onClick={() => toggleSelectMember(u.uid)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: isChecked ? '#f0fdf4' : '#f8fafc', border: isChecked ? '1px solid #bbf7d0' : '1px solid transparent', borderRadius: '14px', marginBottom: '8px', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={u.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="User" style={{ width: '38px', height: '38px', borderRadius: '50%' }} />
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>{displayName}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>{u.phoneNumber}</div>
                        </div>
                      </div>
                      <input type="checkbox" checked={isChecked} onChange={() => {}} style={{ width: '18px', height: '18px', accentColor: '#62b875', cursor: 'pointer' }} />
                    </div>
                  );
                })
              )}
            </div>
            <button onClick={handleAddSelectedMembers} disabled={selectedMemberUids.length === 0 || isUploading} className="btn-green" style={{ width: '100%', opacity: selectedMemberUids.length === 0 ? 0.5 : 1 }}>
              {isUploading ? 'Adding...' : `Add Selected Members (${selectedMemberUids.length})`}
            </button>
          </div>
        </div>
      )}

      {/* ৪. মেম্বারস লিস্ট মডাল */}
      {showMembersModal && selectedCommunity && (
        <div className="modal-overlay" onClick={() => setShowMembersModal(false)}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '800', margin: 0, color: '#0f172a' }}>Community Members</h3>
              <button onClick={() => setShowMembersModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            {isAdmin && (
              <button onClick={() => { setShowMembersModal(false); setShowAddMemberModal(true); }} style={{ flex: 'none', width: '100%', padding: '10px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '14px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', marginBottom: '14px' }}>
                👤+ Add Members from Contact List
              </button>
            )}
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
              {selectedCommunity.members?.map((mUid) => {
                const mUser = allUsersMap[mUid] || {};
                const mRole = mUser.role?.toLowerCase();
                const isLocalAdmin = selectedCommunity.admins?.includes(mUid) || selectedCommunity.adminUid === mUid;
                const isMainAdmin = selectedCommunity.adminUid === currentUid || isGlobalOwner || isGlobalAdmin;
                const normP = normalizePhone(mUser.phoneNumber);
                const mName = localContacts[normP] || mUser.fullName || mUser.phoneNumber || 'User';
                
                // Any community admin can demote another local admin. The main community admin and global roles stay protected.
                const isTargetGlobalAdminOrOwner = mRole === 'owner' || mRole === 'admin';
                const canRemoveAdmin = isAdmin && isLocalAdmin && mUid !== currentUid && mUid !== selectedCommunity.adminUid && !isTargetGlobalAdminOrOwner;
                const canRemove = isAdmin && mUid !== currentUid && !isLocalAdmin && (!isTargetGlobalAdminOrOwner || isGlobalOwner);

                let roleText = 'Member';
                let roleColor = '#94a3b8';
                if (mRole === 'owner') { roleText = 'Owner 👑'; roleColor = '#ef4444'; }
                else if (mRole === 'admin') { roleText = 'Global Admin 🛡️'; roleColor = '#3b82f6'; }
                else if (mRole === 'premium') { roleText = 'Premium 💎'; roleColor = '#eab308'; }
                else if (isLocalAdmin) { roleText = 'Admin 👑'; roleColor = '#62b875'; }
                
                return (
                  <div key={mUid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', pointerEvents: 'none' }}>
                      <img src={mUser.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="Member" style={{ width: '36px', height: '36px', borderRadius: '50%' }} />
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{mName}</div>
                        <div style={{ fontSize: '10px', color: roleColor, fontWeight: '800' }}>{roleText}</div>
                      </div>
                    </div>
                    {isAdmin && mUid !== currentUid && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {!isLocalAdmin && !isTargetGlobalAdminOrOwner && (
                          <button onClick={() => handlePromoteToAdmin(mUid)} style={{ padding: '4px 8px', background: '#e2f4e6', border: 'none', color: '#15803d', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Make Admin</button>
                        )}
                        {canRemoveAdmin && (
                          <button onClick={() => handleRemoveAdmin(mUid)} style={{ padding: '4px 8px', background: '#fff7ed', color: '#ea580c', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Remove Admin</button>
                        )}
                        {canRemove && (
                          <button onClick={() => handleRemoveMember(mUid)} style={{ padding: '4px 8px', background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Remove</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ flex: 'none' }}>
              {!isAdmin && (
                <button onClick={handleLeaveCommunity} style={{ width: '100%', padding: '12px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '8px' }}>🚪 Leave Community</button>
              )}
              {isAdmin && (
                <button onClick={handleDeleteCommunity} style={{ width: '100%', padding: '12px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '14px', fontWeight: '700', cursor: 'pointer' }}>🗑️ Delete Community</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* কমেন্ট ও রিপ্লাই মডাল */}
      {activeCommentPost && (
        <div className="modal-overlay" onClick={() => setActiveCommentPost(null)}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '800', margin: 0, color: '#0f172a' }}>Comments ({commentsList.length})</h3>
              <button onClick={() => setActiveCommentPost(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '12px' }}>
              {commentsList.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '12px', padding: '20px 0' }}>No comments yet.</div>
              ) : (
                commentsList.map((c) => (
                  <div key={c.id} onClick={(e) => handleCommentClick(e, c)} style={{ background: '#f8fafc', padding: '10px 12px', borderRadius: '14px', marginBottom: '8px', cursor: isAdmin ? 'pointer' : 'default' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: '700', fontSize: '12px', color: '#0f172a' }}>{c.senderName}</div>
                      <span style={{ fontSize: '10px', color: '#94a3b8' }}>{c.timeStr || ''}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#334155', marginTop: '3px' }}>{c.text}</div>
                    <button onClick={() => setReplyToComment(c)} style={{ background: 'none', border: 'none', color: '#62b875', fontSize: '11px', fontWeight: '700', cursor: 'pointer', padding: '4px 0 0', marginTop: '2px' }}>Reply</button>
                  </div>
                ))
              )}
            </div>
            {replyToComment && (
              <div style={{ background: '#f0fdf4', padding: '6px 12px', borderRadius: '10px', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                <span style={{ color: '#16a34a', fontWeight: '700' }}>Replying to @{replyToComment.senderName}</span>
                <button onClick={() => setReplyToComment(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>✕</button>
              </div>
            )}
            <form onSubmit={handleSendComment} style={{ display: 'flex', gap: '8px' }}>
              <input type="text" placeholder="Write a comment..." value={commentInput} onChange={(e) => setCommentInput(e.target.value)} className="input-field" style={{ flex: 1, padding: '10px 14px' }} />
              <button type="submit" className="btn-green" style={{ padding: '10px 16px' }}>➔</button>
            </form>
          </div>
        </div>
      )}

      {commentActionModal && isAdmin && (
        <div className="modal-overlay" onClick={() => setCommentActionModal(null)} style={{ alignItems: 'center', zIndex: 10050 }}>
          <div className="context-menu-card" onClick={e => e.stopPropagation()} style={{ minWidth: '220px' }}>
            <div style={{ padding: '12px 14px', fontSize: '12px', fontWeight: '800', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>Comment by {commentActionModal.senderName || 'User'}</div>
            <div className="context-opt" onClick={() => handleDeleteComment(commentActionModal)} style={{ color: '#ef4444' }}>
              <span>🗑️</span> <span>Delete Comment</span>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Message Details Modal */}
      {msgDetails && selectedCommunity && (
        <div className="modal-overlay" onClick={() => setMsgDetails(null)}>
          <div className="voice-card-bottom" onClick={e => e.stopPropagation()} style={{ borderRadius: '28px', padding: '24px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Message Details</h3>
            <div style={{ flex: 1, overflowY: 'auto', textAlign: 'left' }}>
              <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.6', marginBottom: '16px' }}>
                <p><strong>Sent by:</strong> {msgDetails.senderName || 'Admin'}</p>
                <p><strong>Time:</strong> {msgDetails.timeStr || new Date(msgDetails.createdAt?.seconds * 1000).toLocaleString()}</p>
              </div>

              <div style={{ fontSize: '13px', fontWeight: '800', color: '#15803d', marginBottom: '8px' }}>Seen By:</div>
              {selectedCommunity.members.filter(uid => msgDetails.seenBy?.[uid] || uid === msgDetails.senderId).map(uid => {
                if(uid === msgDetails.senderId) return null; 
                const mUser = allUsersMap[uid] || {};
                const normP = normalizePhone(mUser.phoneNumber);
                const mName = localContacts[normP] || mUser.fullName || mUser.phoneNumber || 'User';
                const reaction = msgDetails.reactions?.[uid];

                return (
                  <div key={uid} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: '13px', fontWeight: '600' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{mName}</span>
                      {reaction && <span style={{ fontSize: '14px' }}>{reaction}</span>}
                    </div>
                    <span style={{ color: '#64748b' }}>Seen</span>
                  </div>
                );
              })}

              <div style={{ fontSize: '13px', fontWeight: '800', color: '#ef4444', marginTop: '20px', marginBottom: '8px' }}>Not Seen Yet:</div>
              {selectedCommunity.members.filter(uid => !msgDetails.seenBy?.[uid] && uid !== msgDetails.senderId).map(uid => {
                const mUser = allUsersMap[uid] || {};
                const normP = normalizePhone(mUser.phoneNumber);
                const mName = localContacts[normP] || mUser.fullName || mUser.phoneNumber || 'User';
                return (
                  <div key={uid} style={{ padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>{mName}</div>
                );
              })}
            </div>
            <button onClick={() => setMsgDetails(null)} style={{ width: '100%', padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '800', marginTop: '16px', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Centered Action Menu Modal */}
      {messageActionModal && (
        <div className="modal-overlay" style={{ alignItems: 'center' }} onClick={() => setMessageActionModal(null)}>
          <div className="context-menu-card" onClick={e => e.stopPropagation()}>
            <div className="context-opt" onClick={() => { setSelectedMessageIds([messageActionModal.id]); setMessageActionModal(null); }}>
              <span>☑️</span> <span>Select Message</span>
            </div>

            {isAdmin && (
              <div className="context-opt" onClick={() => { setMsgDetails(messageActionModal); setMessageActionModal(null); }}>
                <span>ℹ️</span> <span>Message Details</span>
              </div>
            )}
            
            {messageActionModal.text && (
              <div className="context-opt" onClick={() => { navigator.clipboard.writeText(messageActionModal.text); setMessageActionModal(null); showToast("Copied!"); }}>
                <span>📋</span> <span>Copy Text</span>
              </div>
            )}

            {messageActionModal.type === 'meet_card' && (
              <div className="context-opt" onClick={() => { navigator.clipboard.writeText(`@@meet_card|${messageActionModal.companyName}|${messageActionModal.meetTime}|${messageActionModal.meetLink}`); setMessageActionModal(null); showToast("Meeting Card Copied!"); }}>
                <span>📋</span> <span>Copy Meeting Card</span>
              </div>
            )}

            {messageActionModal.type === 'community_invite' && (
              <div className="context-opt" onClick={() => { 
                navigator.clipboard.writeText(`@@community_card|${messageActionModal.communityName}|${messageActionModal.inviteCommunityId}`); 
                setMessageActionModal(null); 
                showToast("Community Link Copied!"); 
              }}>
                <span>📋</span> <span>Copy Invite Card</span>
              </div>
            )}

            {messageActionModal.type === 'sticker' && (
              <div className="context-opt" onClick={() => saveSomeoneSticker(messageActionModal.mediaUrl)}>
                <span>⭐</span> <span>Save Sticker</span>
              </div>
            )}
            
            <div className="context-opt" onClick={() => { setMsgToForward([messageActionModal]); setShowForwardModal(true); setMessageActionModal(null); }}>
              <span>↪️</span> <span>Forward</span>
            </div>

            {isAdmin && (
              <div className="context-opt" style={{ color: '#ef4444' }} onClick={() => { handleDeleteMessage(messageActionModal.id); setMessageActionModal(null); }}>
                <span>🗑️</span> <span>Delete Message</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forward Modal */}
      {showForwardModal && (
        <div className="modal-overlay" onClick={() => { setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); }}>
          <div className="card-modal" onClick={e => e.stopPropagation()} style={{ padding: '16px', background: '#f8fafc', height: '70vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Forward to...</span>
              <button onClick={() => { setShowForwardModal(false); setSelectedForwardIds([]); setMsgToForward(null); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {forwardTargets.map(t => {
                const isSelected = selectedForwardIds.includes(t.id);
                let finalName = t.title || t.name || t.fullName || t.displayName || t.chatName;
                let finalPhoto = t.photoURL || t.groupImage || t.photo || t.avatar;

                if (!t.isGroup && !t.isCommunity) {
                    const pList = t.participants || t.users || [];
                    let fwTargetUid = t.otherUserId || t.uid || t.participantId || t.userId;
                    if (!fwTargetUid && pList.length > 0) fwTargetUid = pList.find(p => p !== currentUid);
                    if (!fwTargetUid && t.id && typeof t.id === 'string' && t.id.includes('_')) fwTargetUid = t.id.split('_').find(p => p !== currentUid);
                    if (!fwTargetUid) fwTargetUid = t.id;
                    const matchedProfile = allUsersMap[fwTargetUid];
                    if (matchedProfile) {
                        const sRole = matchedProfile.role?.toLowerCase();
                        const isSpecial = sRole === 'owner' || sRole === 'admin' || sRole === 'premium';
                        finalPhoto = matchedProfile.photoURL || finalPhoto;
                        if (isSpecial) finalName = matchedProfile.fullName || matchedProfile.name || finalName;
                        else {
                            finalName = matchedProfile.fullName || matchedProfile.name || finalName;
                            if (matchedProfile.phoneNumber) {
                                const normP = normalizePhone(matchedProfile.phoneNumber);
                                if (localContacts[normP]) finalName = localContacts[normP];
                            }
                        }
                    } else if (fwTargetUid) {
                        const normP = normalizePhone(fwTargetUid);
                        if (localContacts[normP]) finalName = localContacts[normP];
                    }
                    finalName = finalName || 'User';
                } else { finalName = finalName || 'Group/Community'; }

                finalPhoto = finalPhoto || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150";

                return (
                  <div key={t.id} onClick={() => {
                    if(isSelected) setSelectedForwardIds(selectedForwardIds.filter(id => id !== t.id));
                    else setSelectedForwardIds([...selectedForwardIds, t.id]);
                  }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img src={finalPhoto} alt="Target" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>{finalName}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{t.isGroup ? 'Group' : t.isCommunity ? 'Community' : 'Chat'}</div>
                      </div>
                    </div>
                    <div className={`custom-checkbox ${isSelected ? 'checked' : ''}`}>{isSelected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</div>
                  </div>
                );
              })}
            </div>
            {selectedForwardIds.length > 0 && (
              <button onClick={handleForwardMessageAction} disabled={isUploading} className="btn-green" style={{ width: '100%', marginTop: '10px' }}>
                {isUploading ? 'Forwarding...' : `Forward to ${selectedForwardIds.length} chats`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ভয়েস রেকর্ডার */}
      {showVoiceModal && (
        <div className="modal-overlay">
          <div className="voice-card-bottom">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Voice Note</span>
              <button onClick={cancelVoiceModal} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b', padding: '0 4px' }}>✕</button>
            </div>

            <div className={`voice-circle ${isRecording ? 'recording' : ''}`} onClick={handleCircleVoiceClick}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '36px' }}>
                {waveBars.map((val, idx) => (
                  <div key={idx} style={{ width: '3px', height: `${val}px`, backgroundColor: isRecording ? '#ef4444' : '#62b875', borderRadius: '3px', transition: 'height 0.05s ease' }} />
                ))}
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '15px', fontWeight: '800', color: isRecording ? '#ef4444' : '#0f172a' }}>
                {isRecording ? `Recording... (${recordingTime}s)` : 'Tap on start record'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={cancelVoiceModal} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#64748b', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
              <button onClick={() => { cancelVoiceModal(); setTimeout(openVoiceModal, 200); }} style={{ flex: 1, padding: '10px', background: '#e2f4e6', border: 'none', borderRadius: '12px', fontWeight: '700', color: '#15803d', cursor: 'pointer', fontSize: '12px' }}>Re-record</button>
            </div>
          </div>
        </div>
      )}

      {/* মেইন স্ক্রিন কন্টেইনার (স্লাইড ইফেক্ট সহ) */}
      <div className={`comm-card ${isPageSliding ? 'slide-out' : ''}`}>
        
        {/* Multi-Select Header Overlay */}
        {selectedMessageIds.length > 0 && selectedCommunity ? (
          <div className="multi-select-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => { setSelectedMessageIds([]); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              <span style={{ fontWeight: '800' }}>{selectedMessageIds.length} Selected</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <button onClick={handleMultiCopy} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }} title="Copy">
                 <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
              <button onClick={handleMultiForward} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }} title="Forward">
                 <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/></svg>
              </button>
              {isAdmin && (
                <button onClick={handleMultiDelete} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#ef4444' }}>🗑️</button>
              )}
            </div>
          </div>
        ) : selectedCommunity ? (
          <div className="comm-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => handleNavigate('/community')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              
              <img src={selectedCommunity.iconURL} alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '14px', objectFit: 'cover' }} />
              
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: '800', margin: 0, color: '#0f172a' }}>{selectedCommunity.name}</h3>
                <span style={{ fontSize: '11px', color: '#62b875', fontWeight: '700' }}>
                  {selectedCommunity.members?.length || 1} Members {isAdmin && '• Admin'}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              {isAdmin && (
                <button onClick={() => setShowAddMemberModal(true)} style={{ background: '#e2f4e6', border: 'none', padding: '6px 12px', borderRadius: '12px', color: '#15803d', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>
                  + Add Member
                </button>
              )}
              <button onClick={() => openEditModal()} style={{ background: 'none', border: 'none', cursor: 'pointer' }} title="Community Info">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="comm-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={() => handleNavigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <h2 style={{ fontSize: '20px', fontWeight: '800', margin: 0, color: '#0f172a' }}>Communities</h2>
            </div>
            <button onClick={() => setShowNewCreateModal(true)} className="btn-green" style={{ padding: '8px 14px', fontSize: '12px' }}>
              + Create
            </button>
          </div>
        )}

        {uploadProgress > 0 && (
          <div className="upload-progress-wrap">
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '800', color: '#15803d', marginBottom: '4px' }}><span>Sending...</span><span>{uploadProgress}%</span></div>
            <div className="upload-progress-bar"><div style={{ width: `${uploadProgress}%` }} /></div>
          </div>
        )}

        {/* কন্টেন্ট এলাকা (শুধুমাত্র এই অংশটি স্ক্রল হবে) */}
        {selectedCommunity ? (
          <div className="scrollable-content">
            
            {posts.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '40px', fontSize: '13px' }}>
                📢 No announcements posted yet.
              </div>
            ) : (
              posts.map((p) => {
                const reactionsMap = p.reactions || {};
                const reactionCount = Object.keys(reactionsMap).length;
                const commentsCount = p.commentsCount || 0;
                const myReaction = reactionsMap[currentUid];
                const isSelected = selectedMessageIds.includes(p.id);

                // Role Badges for chat bubbles
                const senderProfile = allUsersMap[p.senderId] || {};
                const sRole = senderProfile.role?.toLowerCase();
                const isLocalAdmin = selectedCommunity?.admins?.includes(p.senderId) || selectedCommunity?.adminUid === p.senderId;

                let roleBadge = null;
                if (sRole === 'owner') roleBadge = <span style={{ fontSize: '10px', background: '#fef2f2', color: '#ef4444', padding: '1px 6px', borderRadius: '8px', fontWeight: '700', border: '1px solid #fecaca' }}>Owner 👑</span>;
                else if (sRole === 'admin') roleBadge = <span style={{ fontSize: '10px', background: '#eff6ff', color: '#3b82f6', padding: '1px 6px', borderRadius: '8px', fontWeight: '700', border: '1px solid #bfdbfe' }}>Admin 🛡️</span>;
                else if (sRole === 'premium') roleBadge = <span style={{ fontSize: '10px', background: '#fefce8', color: '#eab308', padding: '1px 6px', borderRadius: '8px', fontWeight: '700', border: '1px solid #fef08a' }}>Premium 💎</span>;
                else if (isLocalAdmin) roleBadge = <span style={{ fontSize: '10px', background: '#e2f4e6', color: '#15803d', padding: '1px 6px', borderRadius: '8px', fontWeight: '700', border: '1px solid #bbf7d0' }}>Admin</span>;

                return (
                  <div 
                    id={`comm-post-${p.id}`}
                    data-post-id={p.id}
                    key={p.id} 
                    className="post-bubble"
                    onClick={() => { if (selectedMessageIds.length > 0) toggleSelection(p.id); }}
                    style={{ background: isSelected ? 'rgba(98, 184, 117, 0.22)' : '#ffffff' }}
                  >
                    
                    <div 
                      onClick={(e) => handleMsgClick(e, p)}
                      onTouchStart={(e) => handleLoveTouchStart(e, p.id)}
                      onTouchEnd={handleLoveTouchEnd}
                      onMouseDown={(e) => handleLoveTouchStart(e, p.id)}
                      onMouseUp={handleLoveTouchEnd}
                      style={{ cursor: 'pointer' }}
                    >
                      {(() => {
                        const isPostMe = p.senderId === currentUid;
                        const senderName = p.senderName || senderProfile.fullName || 'User';
                        const senderPhoto = p.senderPhoto || senderProfile.photoURL || (isPostMe ? currentUserData?.photoURL : '') || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150';
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px', justifyContent: isPostMe ? 'flex-end' : 'flex-start', flexDirection: isPostMe ? 'row-reverse' : 'row' }}>
                            <img src={senderPhoto} alt="User" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            <span style={{ fontWeight: '900', fontSize: '13px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '5px' }}>{senderName}{roleBadge}</span>
                            <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: isPostMe ? 0 : 'auto' }}>{p.timeStr || 'Today'}</span>
                          </div>
                        );
                      })()}

                      {firstUnreadIdRef.current === p.id && totalUnreadCountRef.current > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                          <div style={{ background: '#1e293b', color: '#fff', fontSize: '12px', fontWeight: '700', padding: '6px 16px', borderRadius: '16px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                            {totalUnreadCountRef.current} unread messages
                          </div>
                        </div>
                      )}
                      {p.type === 'text' && <div style={{ fontSize: '14px', color: '#1e293b', marginBottom: '8px', pointerEvents: 'auto' }}>{renderTextWithLinks(p.text)}</div>}
                      {p.type === 'image' && <img src={p.mediaUrl} alt="Post media" style={{ width: '100%', borderRadius: '14px', marginBottom: '8px', maxHeight: '240px', objectFit: 'cover' }} />}
                      {p.type === 'video' && <video src={p.audioData || p.mediaUrl} controls style={{ width: '100%', borderRadius: '14px', marginBottom: '8px', maxHeight: '240px' }} />}
                      {p.type === 'sticker' && <img src={p.mediaUrl || p.mediaData} alt="Sticker" style={{ width: '120px', height: '120px', objectFit: 'contain', marginBottom: '8px' }} />}
                      {p.type === 'file' && (
                        <div style={{ marginBottom: '8px' }}>
                          <a href={p.fileData} target="_blank" rel="noopener noreferrer" download={p.fileName} style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '20px' }}>📄</span>
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '13px' }}>{p.fileName}</div>
                              <div style={{ fontSize: '10px', opacity: 0.8 }}>{p.fileSize} • Download</div>
                            </div>
                          </a>
                        </div>
                      )}
                      {p.type === 'voice' && (() => {
                        const vp = voicePlayback[p.id] || {};
                        const dur = Number(vp.duration) || (parseFloat(p.duration) || 0);
                        const cur = Number(vp.current) || 0;
                        const progressRatio = dur > 0 ? Math.min(1, cur / dur) : 0;
                        const bars = Array.isArray(p.waveform) && p.waveform.length ? p.waveform : makeFallbackWaveform(p.id);
                        const isPostMe = p.senderId === currentUid;
                        const avatar = p.senderPhoto || (isPostMe ? currentUserData?.photoURL : (senderProfile?.photoURL || selectedCommunity?.iconURL)) || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80';
                        return (
                          <div className={`voice-message-card ${isPostMe ? 'voice-me' : 'voice-other'}`} style={{ marginBottom:'8px' }}>
                            <button type="button" className="voice-play-btn" onClick={(e) => { e.stopPropagation(); toggleVoicePlayback(p.id); }}>
                              {playingVoiceId === p.id ? '❚❚' : '▶'}
                            </button>
                            <div className="voice-wave-wrap" onClick={(e) => { e.stopPropagation(); toggleVoicePlayback(p.id); }}>
                              <div className="voice-wave">
                                {bars.map((h,i)=><span key={i} style={{height:`${h}px`, background: i / bars.length <= progressRatio ? (isPostMe ? '#ffffff' : '#22c55e') : (isPostMe ? 'rgba(255,255,255,.55)' : '#94a3b8')}} />)}
                              </div>
                              <div className="voice-meta"><span>{formatVoiceTime(cur || (playingVoiceId === p.id ? 0 : dur))}</span><span>{p.timeStr || 'Today'}</span></div>
                            </div>
                            <img className="voice-avatar" src={avatar} alt="" />
                            <audio id={`voice-${p.id}`} src={p.audioData} preload="metadata" onPlay={handleAudioPlay} onPause={() => { if (playingVoiceId === p.id) setPlayingVoiceId(null); }} onLoadedMetadata={(e)=>handleVoiceLoadedMetadata(e,p.id)} onTimeUpdate={(e)=>handleVoiceTimeUpdate(e,p.id)} onEnded={()=>handleVoiceEnded(p.id)} />
                          </div>
                        );
                      })()}
                      {p.type === 'meet_card' && (
                        <div className="magic-meet-wrapper" style={{ margin: '0 auto 10px' }}>
                          <div className="magic-meet-header">
                            <h2 className="magic-meet-title">LIVE MEETING</h2>
                            <div className="magic-meet-subtitle">আপনার উপস্থিতি আমাদের জন্য গুরুত্বপূর্ণ</div>
                          </div>
                          <div className="magic-meet-timebox">
                            <div>আজ <b>{p.meetTime}</b></div>
                            <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '12px' }}>জয়েন হওয়ার সময় <b>১০ মিনিট</b></div>
                          </div>
                          <div className="magic-meet-steps">
                            <div style={{ textAlign: 'center', marginBottom: '8px', color: '#16a34a', fontWeight: '900' }}>জয়েন করার ধাপ:</div>
                            <div className="magic-step-item"><span className="magic-step-no">01</span> Meet এ ক্লিক করুন</div>
                            <div className="magic-step-item"><span className="magic-step-no">02</span> Gmail দিয়ে সাইন ইন করুন</div>
                            <div className="magic-step-item"><span className="magic-step-no">03</span> "Ask to Join" বাটনে ক্লিক করুন</div>
                            <div className="magic-step-item"><span className="magic-step-no">04</span> মিটিংয়ে জয়েন করে স্ক্রিনশট ইনবক্সে পাঠান</div>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); window.open(p.meetLink, '_blank'); }} className="magic-join-btn">JOIN MEETING</button>
                        </div>
                      )}
                      {p.type === 'group_invite' && (
                        <div className="magic-meet-wrapper" style={{ margin: '0 auto 10px' }}>
                          <div className="magic-meet-header" style={{ background: '#fef3c7', borderBottom: '2px dashed #fde047' }}>
                            <h2 className="magic-meet-title" style={{ color: '#854d0e' }}>GROUP INVITE</h2>
                            <div className="magic-meet-subtitle" style={{ color: '#a16207', borderColor: '#fde047' }}>আপনাকে গ্রুপে আমন্ত্রণ জানানো হয়েছে</div>
                          </div>
                          <div style={{ padding: '16px', fontWeight: '900', fontSize: '18px', color: '#0f172a' }}>{p.groupName}</div>
                          <button onClick={(e) => { e.stopPropagation(); handleNavigate('/group', { state: { groupId: p.inviteGroupId }}); }} className="magic-join-btn" style={{ background: '#ca8a04', boxShadow: '0 4px 15px rgba(202, 138, 4, 0.4)' }}>JOIN GROUP</button>
                        </div>
                      )}
                      {p.type === 'community_invite' && (
                        <div className="magic-meet-wrapper" style={{ margin: '0 auto 10px' }}>
                          <div className="magic-meet-header" style={{ background: '#e0f2fe', borderBottom: '2px dashed #bae6fd' }}>
                            <h2 className="magic-meet-title" style={{ color: '#0369a1' }}>COMMUNITY INVITE</h2>
                            <div className="magic-meet-subtitle" style={{ color: '#0284c7', borderColor: '#bae6fd' }}>আপনাকে কমিউনিটিতে আমন্ত্রণ জানানো হয়েছে</div>
                          </div>
                          <div style={{ padding: '16px', fontWeight: '900', fontSize: '18px', color: '#0f172a' }}>{p.communityName}</div>
                          <button onClick={(e) => { e.stopPropagation(); handleJoinCommunity(p.inviteCommunityId); }} className="magic-join-btn" style={{ background: '#0284c7', boxShadow: '0 4px 15px rgba(2, 132, 199, 0.4)' }}>
                            JOIN COMMUNITY
                          </button>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#64748b', fontWeight: '600', paddingBottom: '6px' }}>
                        <div>{reactionCount > 0 ? <span>❤️👍 {reactionCount > 1 ? `You + ${reactionCount - 1}` : 'Reacted'}</span> : <span style={{ opacity: 0.6 }}>No reactions yet</span>}</div>
                        <div>{commentsCount > 0 ? `${commentsCount} comments` : ''}</div>
                      </div>
                    </div>

                    {/* Action Bar inside Bubble */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                      <button 
                        onClick={() => handleToggleLoveReaction(p.id, p.reactions)}
                        style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: myReaction ? '#ef4444' : '#64748b', fontWeight: '700', fontSize: '13px' }}
                      >
                        <span style={{ fontSize: '16px' }}>{myReaction || '❤️'}</span>
                        <span>{myReaction ? 'Reacted' : 'Love'}</span>
                      </button>

                      <button onClick={() => openComments(p)} style={{ background: 'none', border: 'none', color: '#62b875', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>💬</span> <span>Comments</span>
                      </button>
                    </div>

                  </div>
                );
              })
            )}

            <div ref={postsEndRef} />
          </div>
        ) : (
          <div className="scrollable-content">
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '12px' }}>
              YOUR JOINED COMMUNITIES
            </div>

            {communities.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '40px', fontSize: '13px' }}>
                No joined communities found. Click '+ Create' to build one!
              </div>
            ) : (
              communities.map((c) => (
                <div 
                  key={c.id} 
                  onClick={() => {
                    handleNavigate(`/community/${c.id}`, { state: { communityId: c.id } });
                    setSelectedCommunity(c);
                    setMessages(getCommunityCache(c.id).messages); 
                    isInitialLoadRef.current = true; justMounted.current = true;
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: 'white', borderRadius: '20px', marginBottom: '10px', border: '1px solid #f1f5f9', cursor: 'pointer' }}
                >
                  <img src={c.iconURL} alt="Logo" style={{ width: '48px', height: '48px', borderRadius: '16px', objectFit: 'cover' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ fontSize: '15px', fontWeight: '800', margin: '0 0 2px', color: '#0f172a' }}>{c.name}</h4>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description}</p>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                </div>
              ))
            )}
          </div>
        )}

        {/* ফুটার (ফিক্সড থাকবে) - Only for Admins */}
        {selectedCommunity && (
          <React.Fragment>
            {isAdmin ? (
              <form onSubmit={handlePostAnnouncement} className="admin-footer-bar">
                <input type="file" ref={fileInputRef} accept="image/*,video/*" onChange={(e) => setPostImageFile(e.target.files[0])} style={{ display: 'none' }} />
                <input type="file" ref={docInputRef} accept="*" onChange={(e) => setPostDocFile(e.target.files[0])} style={{ display: 'none' }} />

                <div className="footer-pill-input">
                  <span onClick={() => setShowStickerModal(true)} style={{ cursor: 'pointer', fontSize: '18px', marginRight: '6px' }}>😊</span>

                  <textarea
                    ref={textareaRef}
                    rows="1"
                    placeholder={postImageFile ? `Media attached` : postDocFile ? `File attached` : "Write an announcement..."} 
                    value={postText}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none', color: '#0f172a', outline: 'none', fontSize: '14px', resize: 'none', maxHeight: '120px', overflowY: 'auto' }}
                  />

                  <button type="button" onClick={() => docInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }} title="Attach File">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </button>

                  <button type="button" onClick={() => fileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }} title="Attach Photo/Video">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  </button>
                </div>

                {postText.trim() || postImageFile || postDocFile ? (
                  <button type="submit" disabled={isUploading} className="dark-circle-send-btn">
                    {isUploading ? <span style={{ fontSize: '12px' }}>...</span> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" style={{ transform: 'translateY(1px)' }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
                  </button>
                ) : (
                  <button type="button" onClick={openVoiceModal} className="dark-circle-send-btn" title="Voice Note">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" style={{ transform: 'translateY(1px)' }}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  </button>
                )}
              </form>
            ) : (
              <div className="admin-footer-bar" style={{ justifyContent: 'center' }}>
                <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>
                  🔒 Only admins can post announcements. You can react & comment above.
                </span>
              </div>
            )}
          </React.Fragment>
        )}

      </div>
    </div>
  );
};

const styles = {
  container: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#fafafa', display: 'flex', justifyContent: 'center', alignItems: 'center' }
};

export default CommunityScreen;